import { useState } from "react";
import { BadgeCheck, Building2, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import LocationEditor from "../../components/company/LocationEditor";
import LocationReports from "../../components/company/LocationReports";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { useCompany } from "../../components/layout/CompanyLayout";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import { ConfirmButton } from "../../components/common/ConfirmButton";
import CrudDialog from "../../components/common/CrudDialog";

const KEYS = ["name", "code", "address", "city", "country", "phone", "email", "hours", "latitude", "longitude", "manager_id", "status"];
const EMPTY = { name: "", code: "", address: "", city: "", country: "", phone: "", email: "", hours: "", latitude: "", longitude: "", manager_id: "", status: "active" };
const buildFields = (team) => [
  { name: "name", label: "Nom de l'agence", required: true, half: true },
  { name: "code", label: "Code interne", half: true },
  { name: "address", label: "Adresse" },
  { name: "city", label: "Ville", half: true },
  { name: "country", label: "Pays", half: true },
  { name: "phone", label: "Téléphone", half: true, inputType: "tel" },
  { name: "email", label: "Email", half: true, inputType: "email" },
  { name: "hours", label: "Horaires (ex : Lun-Ven 8h-17h)" },
  { name: "latitude", label: "Latitude GPS", half: true, nullable: true, inputType: "number" },
  { name: "longitude", label: "Longitude GPS", half: true, nullable: true, inputType: "number" },
  { name: "manager_id", label: "Responsable", type: "select", nullable: true, half: true, options: [["", "Aucun"], ...team.map((m) => [m.user_id, m.name])] },
  { name: "status", label: "Statut", type: "select", half: true, options: [["active", "Active"], ["inactive", "Désactivée"]] },
];

export default function Agencies() {
  const { can } = useCompany();
  const { data, loading, error, reload } = useApi("/company/agencies");
  const team = useApi("/company/team");
  const memberName = (id) => team.data?.find((m) => m.user_id === id)?.name;
  const [editing, setEditing] = useState(null);
  const [locating, setLocating] = useState(null);
  const [tick, setTick] = useState(0);
  const refresh = () => { reload(); setTick((t) => t + 1); };
  const openLocate = (id) => setLocating(data?.find((a) => a.id === id) || null);
  const manage = can("structure:update");
  const canCreate = can("structure:create");
  const canDelete = can("structure:delete");

  const remove = async (a) => {
    try { await api.delete(`/company/agencies/${a.id}`); toast.success("Agence supprimée"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Agences" description="Établissements, guichets ou sites physiques de votre organisation."
        actions={canCreate && <Button onClick={() => setEditing(EMPTY)} className="rounded-full" data-testid="agency-create-button"><Plus className="mr-1.5 h-4 w-4" />Nouvelle agence</Button>} />
      {manage && <LocationReports onLocate={openLocate} refreshKey={tick} />}
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.length && <EmptyState icon={Building2} title="Aucune agence" text="Ajoutez vos sites pour rattacher les feedbacks au bon établissement." />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data?.map((a) => (
          <div key={a.id} className="card-surface p-5" data-testid={`agency-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2"><p className="font-display font-semibold">{a.name}{a.code && <span className="ml-2 font-mono text-xs text-slate-400">{a.code}</span>}</p>
              <span className={`rounded-full px-2 py-0.5 text-[11px] ${a.status === "inactive" ? "bg-slate-200 text-slate-600" : "bg-emerald-50 text-emerald-700"}`} data-testid={`agency-status-${a.id}`}>{a.status === "inactive" ? "Désactivée" : "Active"}</span></div>
            <p className="mt-1 text-sm text-slate-500">{[a.address, a.city, a.country].filter(Boolean).join(", ") || "Adresse non renseignée"}</p>
            <div className="mt-2 space-y-0.5 text-xs text-slate-500">
              {(a.phone || a.email) && <p>{[a.phone, a.email].filter(Boolean).join(" · ")}</p>}
              {a.hours && <p>Horaires : {a.hours}</p>}
              {a.manager_id && <p>Responsable : {memberName(a.manager_id) || "—"}</p>}
              {a.latitude != null && a.longitude != null
                ? <p className="flex items-center gap-1" data-testid={`agency-location-${a.id}`}>{a.location_verified_at ? <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" /> : <MapPin className="h-3.5 w-3.5" />}<a href={`https://www.openstreetmap.org/?mlat=${a.latitude}&mlon=${a.longitude}#map=17/${a.latitude}/${a.longitude}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid={`agency-map-${a.id}`}>{a.latitude}, {a.longitude}</a>{a.location_verified_at && <span className="text-emerald-700">· vérifiée</span>}</p>
                : <p className="text-amber-700" data-testid={`agency-location-${a.id}`}>Position GPS non renseignée : absente de la carte</p>}
            </div>
            {manage && (
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(a)} data-testid={`agency-edit-${a.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Modifier</Button>
                <Button variant="outline" size="sm" onClick={() => setLocating(a)} data-testid={`agency-locate-${a.id}`}><MapPin className="mr-1 h-3.5 w-3.5" />Position</Button>
                {canDelete && <ConfirmButton testId={`agency-delete-${a.id}`} title="Supprimer cette agence ?" description="Les services et QR codes rattachés seront détachés. Impossible si des feedbacks y sont liés."
                  confirmLabel="Supprimer" onConfirm={() => remove(a)}
                  trigger={<Button variant="ghost" size="sm" className="text-rose-600" data-testid={`agency-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>} />}
              </div>
            )}
          </div>
        ))}
      </div>
      {locating && <LocationEditor agency={locating} onClose={() => setLocating(null)} onSaved={refresh} />}
      {editing && (
        <CrudDialog open onOpenChange={(o) => { if (!o) { setEditing(null); } }} testId="agency" fields={buildFields(team.data || [])}
          title={editing.id ? "Modifier l'agence" : "Nouvelle agence (un QR code sera généré)"} initial={Object.fromEntries(KEYS.map((k) => [k, editing[k] ?? EMPTY[k]]))}
          endpoint={editing.id ? `/company/agencies/${editing.id}` : "/company/agencies"} method={editing.id ? "put" : "post"} onSaved={refresh} />
      )}
    </div>
  );
}
